import { useState } from "react";
import { MessageSquarePlus, Bug, Lightbulb, MessageCircle, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { trackEvent } from "@/lib/analytics";

type Category = "bug" | "improvement" | "general" | "feature";

const categories: { key: Category; label: string; icon: typeof Bug; color: string }[] = [
  { key: "bug", label: "Bug Report", icon: Bug, color: "text-red-600" },
  { key: "improvement", label: "Suggest Improvement", icon: Lightbulb, color: "text-amber-600" },
  { key: "general", label: "General Feedback", icon: MessageCircle, color: "text-primary" },
  { key: "feature", label: "Feature Request", icon: Sparkles, color: "text-violet-600" },
];

function detectDevice(): string {
  if (typeof navigator === "undefined") return "unknown";
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return "iOS";
  if (/Android/.test(ua)) return "Android";
  if (/Mobi/.test(ua)) return "Mobile";
  return "Desktop";
}

export function FloatingFeedbackButton() {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState<Category | null>(null);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { user } = useAuth();

  const submit = async () => {
    if (!category || message.trim().length < 3) {
      toast.error("Pick a category and add a short message.");
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.from("beta_feedback").insert({
      user_id: user?.id ?? null,
      category,
      message: message.trim().slice(0, 2000),
      page_path: window.location.pathname,
      user_agent: navigator.userAgent.slice(0, 500),
      device: detectDevice(),
    });
    setSubmitting(false);
    if (error) {
      toast.error("Couldn't send feedback. Please try again.");
      return;
    }
    trackEvent("feedback_submitted", { category });
    toast.success("Thanks! Your feedback helps make We-Help better.");
    setMessage("");
    setCategory(null);
    setOpen(false);
  };

  return (
    <>
      <Button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Send feedback"
        className="fixed bottom-4 right-4 z-40 h-12 w-12 rounded-full p-0 shadow-lg md:h-auto md:w-auto md:px-4 md:py-3"
      >
        <MessageSquarePlus className="h-5 w-5" />
        <span className="hidden md:inline">Feedback</span>
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Send Beta Feedback</DialogTitle>
            <DialogDescription>
              Tell us what's broken, what's confusing, or what you'd love next.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-2 grid grid-cols-2 gap-2">
            {categories.map((c) => (
              <button
                key={c.key}
                type="button"
                onClick={() => setCategory(c.key)}
                className={`flex flex-col items-start gap-1 rounded-lg border p-3 text-left transition min-h-[44px] ${
                  category === c.key
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-primary/50"
                }`}
              >
                <c.icon className={`h-4 w-4 ${c.color}`} />
                <span className="text-sm font-medium text-foreground">{c.label}</span>
              </button>
            ))}
          </div>

          <div className="mt-4">
            <label htmlFor="fb-message" className="text-sm font-medium text-foreground">
              Your message
            </label>
            <Textarea
              id="fb-message"
              value={message}
              onChange={(e) => setMessage(e.target.value.slice(0, 2000))}
              placeholder="What happened? What did you expect?"
              rows={4}
              className="mt-1"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              {message.length}/2000 · Page: {typeof window !== "undefined" ? window.location.pathname : ""}
            </p>
          </div>

          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={submitting}>
              {submitting ? "Sending…" : "Send feedback"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default FloatingFeedbackButton;